import {
  ADS_CAMPAIGN_META_DESTINATION_TYPE_LEAD_FROM_IG_DIRECT_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_INSTAGRAM_PROFILE_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_POST_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_VIDEO_OPTION,
  ADS_CAMPAIGN_TEMPLATE_MULTIPLE_OPTION,
  IMAGE_FORMATS,
  VIDEO_FORMATS,
} from "./constants";
import { getExtensionFile } from "./string";

/** width/height: 1:1 — mirrors BE imageResizing.js */
const SQUARE_ASPECT_RATIO = 1;
/** width/height: 4:5 (IG feed) — mirrors BE imageResizing.js */
const FEED_ASPECT_RATIO = 4 / 5;
/** Tolerate small deviations — mirrors BE imageResizing.js */
const ASPECT_RATIO_EPSILON = 0.02;

const resolveMediaUrl = (media) => {
  if (!media) return "";
  if (typeof media === "string") return media;
  if (typeof media?.url === "string") return media.url;
  if (typeof media?.url?.value === "string") return media.url.value;
  return "";
};

const isCarouselMedia = (media) => {
  if (!media || typeof media === "string") return false;
  return Boolean(
    media.isCarousel ||
      media.type === "carousel" ||
      (Array.isArray(media.carouselImages) && media.carouselImages.length > 1)
  );
};

const isEligibleStaticImageUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  const extension = getExtensionFile(url);
  if (!extension) return false;
  if (VIDEO_FORMATS.includes(extension)) return false;
  return IMAGE_FORMATS.includes(extension);
};

const isDestinationBlockedForAdapt = (destinationType) =>
  destinationType === ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_POST_OPTION ||
  destinationType === ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_VIDEO_OPTION ||
  destinationType ===
    ADS_CAMPAIGN_META_DESTINATION_TYPE_LEAD_FROM_IG_DIRECT_OPTION ||
  destinationType ===
    ADS_CAMPAIGN_META_DESTINATION_TYPE_INSTAGRAM_PROFILE_OPTION;

/**
 * Meta feed/square placements accept creatives from ~4:5 through ~1:1.
 * Mirrors BE coversSquarePlacements.
 * @param {{ ratio: number }} params
 * @returns {boolean}
 */
export const coversSquarePlacements = ({ ratio } = {}) => {
  if (typeof ratio !== "number" || !(ratio > 0)) return false;
  const min = FEED_ASPECT_RATIO - ASPECT_RATIO_EPSILON;
  const max = SQUARE_ASPECT_RATIO + ASPECT_RATIO_EPSILON;
  return ratio >= min && ratio <= max;
};

/**
 * Taller than feed (e.g. 9:16): already covers vertical placement groups.
 * Mirrors BE coversVerticalPlacements.
 * @param {{ ratio: number }} params
 * @returns {boolean}
 */
export const coversVerticalPlacements = ({ ratio } = {}) => {
  if (typeof ratio !== "number" || !(ratio > 0)) return false;
  return ratio < FEED_ASPECT_RATIO - ASPECT_RATIO_EPSILON;
};

/**
 * Mirror BE getImageFormatFromDimensions (square|vertical|horizontal|feed).
 */
export const getImageFormatFromDimensions = ({ width, height } = {}) => {
  if (!width || !height || width <= 0 || height <= 0) {
    return null;
  }
  const ratio = width / height;
  if (Math.abs(ratio - SQUARE_ASPECT_RATIO) <= ASPECT_RATIO_EPSILON) {
    return "square";
  }
  if (ratio > 1) return "horizontal";
  if (Math.abs(ratio - FEED_ASPECT_RATIO) <= ASPECT_RATIO_EPSILON) {
    return "feed";
  }
  return "vertical";
};

/**
 * Missing square/vertical format count for placement adapt (matches BE).
 * Prefer ratio so the band between 4:5 and 1:1 counts as covering square.
 * @param {{ format?: string|null, ratio?: number }} params
 * @returns {number}
 */
export const getMissingSquareVerticalFormatsCount = ({
  format,
  ratio,
} = {}) => {
  if (typeof ratio === "number" && ratio > 0) {
    if (coversSquarePlacements({ ratio })) return 1;
    if (coversVerticalPlacements({ ratio })) return 1;
    return 2;
  }

  if (format === "square" || format === "feed" || format === "vertical") {
    return 1;
  }
  return 2;
};

const loadImageDimensions = (url) =>
  new Promise((resolve) => {
    if (typeof Image === "undefined" || !url) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

/**
 * Sum of missing square/vertical formats for unique eligible URLs.
 * Unknown dimensions → 2 (conservative).
 */
export const estimateAdaptFormatsMultiplier = async (uniqueUrls = []) => {
  if (!Array.isArray(uniqueUrls) || uniqueUrls.length === 0) {
    return 0;
  }

  const counts = await Promise.all(
    uniqueUrls.map(async (url) => {
      const dimensions = await loadImageDimensions(url);
      if (!dimensions) {
        return 2;
      }
      const ratio = dimensions.width / dimensions.height;
      const format = getImageFormatFromDimensions(dimensions);
      return getMissingSquareVerticalFormatsCount({ format, ratio });
    })
  );

  return counts.reduce((sum, count) => sum + count, 0);
};

/**
 * Collect unique eligible static image URLs for Meta placement adaptation.
 */
export const collectUniqueEligibleImageUrls = ({
  template,
  mediaUrls = [],
  multipleItems = [],
} = {}) => {
  const sources =
    template === ADS_CAMPAIGN_TEMPLATE_MULTIPLE_OPTION
      ? (multipleItems || []).flatMap((item) => item?.mediaUrls || [])
      : mediaUrls || [];

  const unique = [];
  const seen = new Set();

  sources.forEach((media) => {
    if (isCarouselMedia(media)) return;
    const url = resolveMediaUrl(media);
    if (!isEligibleStaticImageUrl(url)) return;
    if (seen.has(url)) return;
    seen.add(url);
    unique.push(url);
  });

  return unique;
};

/**
 * Whether the Meta onboarding content step should show the adapt checkbox.
 * Visibility depends only on campaign destination (not on selected media).
 */
export const getMetaAdaptPlacementsEligibility = ({
  template,
  mediaUrls = [],
  multipleItems = [],
  destinationType,
} = {}) => {
  const uniqueUrls = collectUniqueEligibleImageUrls({
    template,
    mediaUrls,
    multipleItems,
  });

  if (isDestinationBlockedForAdapt(destinationType)) {
    return {
      shouldShow: false,
      uniqueUrls: [],
    };
  }

  return {
    shouldShow: true,
    uniqueUrls,
  };
};

export {
  resolveMediaUrl,
  isCarouselMedia,
  isEligibleStaticImageUrl,
  isDestinationBlockedForAdapt,
  SQUARE_ASPECT_RATIO,
  FEED_ASPECT_RATIO,
  ASPECT_RATIO_EPSILON,
};
